import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ControlValueAccessor } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'lib-vinnova-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DropdownComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  readonly = input<boolean>();
  placeholder = input<string>();
  id = input<string>();
  multiple = input<boolean>();
  isSearch = input<boolean>();
  disabled = input<boolean>();
  label = input<string>();
  langKey = input<boolean>();
  max = input<number>();
  items = input<DropdownItem[]>();
  error = input<boolean>();
  required = input<boolean>();

  private readonly activeLang = signal<string>('sv');
  readonly resolvedPlaceholder = computed(() => {
    const placeholder = this.placeholder()?.trim();
    return placeholder || (this.activeLang() === 'sv' ? 'Välj' : 'Select');
  });

  itemsFromInput = computed(() => {
    if (this.items()?.length > 0) {
      const itemsFromInput = [...this.items()];
      if (itemsFromInput?.length > 0) {
        this.setSelectedItems(itemsFromInput);
      }
      return itemsFromInput;
    }
  });
  isDisabled = signal(false);
  isMobile: boolean;
  selectedItems: string;
  selectedMobileLabel: string;
  numberOfchoosenItems = 0;
  focus: boolean;
  placeholderValue: DropdownItem = { value: '', label: '' };

  openDropdown = output<void>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any | any[];
  dropdownRef = viewChild<NgSelectComponent>('dropdown');
  dropdownContainerRef = viewChild<ElementRef>('dropdownContainer');

  constructor(
    private cdr: ChangeDetectorRef,
    public translateService: TranslocoService,
  ) {
    effect(() => {
      const element =
        this.isMobile && !this.multiple()
          ? this.dropdownContainerRef()?.nativeElement.querySelector(
              '.mobile-dropdown select',
            )
          : this.dropdownContainerRef()?.nativeElement.querySelector(
              'ng-select',
            );

      if (this.error() && element) {
        element.classList.add('invalid');
      } else if (element) {
        element.classList.remove('invalid');
      }

      if (this.required() && element) {
        element.setAttribute('aria-required', 'true');
      } else if (element) {
        element.removeAttribute('aria-required');
      }
    });
  }

  ngOnInit() {
    this.activeLang.set(this.translateService.getActiveLang());
    this.translateService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe((lang) => this.activeLang.set(lang));

    if (!this.multiple() && !this.required()) {
      this.translateService.langChanges$
        .pipe(untilDestroyed(this))
        .subscribe((val) => {
          this.placeholderValue.label = val === 'sv' ? 'Välj' : 'Choose';
        });
    }
    this.isMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        navigator.userAgent,
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: string) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(obj: any): void {
    this.value = this.multiple() && obj ? JSON.parse(obj) : obj;
    this.setSelectedItems(this.itemsFromInput());
    this.cdr.detectChanges();
  }

  onOpen() {
    this.openDropdown.emit();
  }

  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngAfterViewInit(): void {
    this.dropdownContainerRef()
      ?.nativeElement.querySelector('input')
      ?.setAttribute('autocomplete', 'off');

    if (this.id()) {
      this.dropdownContainerRef()
        ?.nativeElement.querySelector('input')
        ?.setAttribute('aria-describedby', `selecteditems_${this.id()}`);
    }
    const accessibleName = this.label() ?? this.resolvedPlaceholder();
    if (accessibleName) {
      this.dropdownContainerRef()
        ?.nativeElement.querySelector('input')
        ?.setAttribute('aria-label', accessibleName);
    }
  }

  onClose() {
    this.dropdownRef().blur();
  }

  onDropdownChange($event?: Event) {
    if ($event) {
      const selected = [...($event.target as HTMLSelectElement).options].filter(
        (o) => o.selected,
      );
      if (!selected) {
        return;
      }
      this.value = this.multiple()
        ? selected.map((o) => o.value)
        : selected[0].value;
    }
    if (this.value === '' || !this.value) {
      this.value = undefined;
    }
    const input =
      this.dropdownContainerRef()?.nativeElement.querySelector(
        '.ng-input input',
      );
    if (input) {
      input.value = '';
    }
    this.onChange(this.multiple() ? JSON.stringify(this.value) : this.value);
    this.setSelectedItems(this.itemsFromInput());
    this.focus = false;
  }

  onBlur() {
    if (!this.isDisabled()) {
      this.onTouched();
    }
    this.focus = false;
  }

  onKeyDown($event) {
    if (
      $event.key === 'Enter' ||
      ($event.key as string).startsWith('Arrow') ||
      $event.key === 'Tab'
    ) {
      return;
    }
    this.focus = true;
  }

  private setSelectedItems(items: DropdownItem[]) {
    if (this.multiple()) {
      const choosenItems = items?.filter((i) =>
        this.value?.includes(i.value.toString()),
      );
      this.numberOfchoosenItems = choosenItems?.length;
      const choosenItem =
        this.value?.length > 0
          ? items?.filter((i) => this.value[0]?.includes(i.value.toString()))
          : [];

      const suffix =
        choosenItems?.length > 1 ? ' +' + (choosenItems?.length - 1) : '';

      if (this.langKey() && choosenItems?.length > 0) {
        this.selectedItems =
          this.translateService.translate(choosenItem[0]?.label) + suffix;
      } else if (choosenItems?.length > 0) {
        this.selectedItems = choosenItem[0]?.label + suffix;
      } else {
        this.selectedItems = '';
      }
    } else {
      const item = items?.find(
        (i) => i.value?.toString() === this.value?.toString(),
      );
      this.selectedItems = this.langKey()
        ? this.translateService.translate(item?.label)
        : item?.label;
      if (this.isMobile) {
        this.selectedMobileLabel = item?.selectedLabel ?? item?.label;
      }
    }
  }
}

export interface DropdownItem {
  value: string;
  label: string;
  selectedLabel?: string;
  subtitle?: string;
  disabled?: boolean;
}
